import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Box,
  Button,
  Snackbar,
  Alert
} from '@mui/material';
import { Close, Info, Settings, History, Build } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  lockScooterAsync,
  formatCDScooterAsync,
  deleteScooterAsync,
  signalScooterAsync,
  setBatteryCoverAsync
} from '../../redux/slices/scootersSlice';
import InformationContent from './components/InformationContent';
import SettingsContent from './components/SettingsContent';
import HistoryContent from './components/HistoryContent';
import NotesContent from './components/NotesContent';
import { Props } from './interface';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scooter-settings-tabpanel-${index}`}
      aria-labelledby={`scooter-settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const ScooterSettingsModal = ({ open, onClose, scooter, isSuperAdmin, container }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSignal = async () => {
    if (!scooter) return;
    
    try {
      await dispatch(signalScooterAsync(scooter._id, { value: true }) as any);
      setSnackbar({
        open: true,
        message: t('scooterModal.signalSent'),
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('scooterModal.signalError'),
        severity: 'error'
      });
    }
  };

  const handleLockToggle = async () => {
    if (!scooter) return;
    
    try {
      await dispatch(lockScooterAsync(scooter._id, { lock: !scooter.lock }) as any);
      setSnackbar({
        open: true,
        message: scooter.lock ? t('scooterModal.scooterUnlocked') : t('scooterModal.scooterLocked'),
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('scooterModal.lockError'),
        severity: 'error'
      });
    }
  };

  const handleBatteryCover = async (value: boolean) => {
    if (!scooter) return;
    const action = value ? t('scooterModal.batteryOpenAction') : t('scooterModal.batteryCloseAction');
    if (!window.confirm(t('scooterModal.batteryConfirm', { action }))) return;
    try {
      await dispatch(setBatteryCoverAsync(scooter._id, value) as any);
      setSnackbar({
        open: true,
        message: value ? t('scooterModal.batteryOpenSent') : t('scooterModal.batteryCloseSent'),
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('scooterModal.batteryError'),
        severity: 'error'
      });
    }
  };

  const handleRestart = async () => {
    if (!scooter) return;
    
    if (window.confirm(t('scooterModal.restartConfirm'))) {
      try {
        await dispatch(formatCDScooterAsync(scooter._id) as any);
        setSnackbar({
          open: true,
          message: t('scooterModal.restartSuccess'),
          severity: 'success'
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: t('scooterModal.restartError'),
          severity: 'error'
        });
      }
    }
  };

  const handleDelete = async () => {
    if (!scooter) return;
    
    if (window.confirm(t('scooterModal.deleteConfirm'))) {
      try {
        await dispatch(deleteScooterAsync(scooter._id) as any);
        setSnackbar({
          open: true,
          message: t('scooterModal.deleteSuccess'),
          severity: 'success'
        });
        onClose();
      } catch (error) {
        setSnackbar({
          open: true,
          message: t('scooterModal.deleteError'),
          severity: 'error'
        });
      }
    }
  };

  const handleSave = () => {
    setSnackbar({
      open: true,
      message: t('scooterModal.saveSuccess'),
      severity: 'success'
    });
  };

  const handleBrokenStatusChange = () => {
    setActiveTab(3);
    setSnackbar({
      open: true,
      message: t('scooterModal.brokenNoteHint'),
      severity: 'info'
    });
  };

  const handleClose = () => {
    setActiveTab(0);
    setSnackbar({ open: false, message: '', severity: 'success' });
    onClose();
  };

  const tabs = [
    { label: t('scooterModal.tabInfo'), icon: <Info fontSize="small" /> },
    { label: t('scooterModal.tabSettings'), icon: <Settings fontSize="small" /> },
    { label: t('scooterModal.tabUserHistory'), icon: <History fontSize="small" /> },
    { label: t('scooterModal.tabRepairHistory'), icon: <Build fontSize="small" /> }
  ];

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="lg" 
        fullWidth
        container={container}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
          <Typography variant="h6">
            {t('scooterModal.title', { name: scooter?.name })}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 64,
                textTransform: 'none',
                fontSize: '0.95rem',
                whiteSpace: 'nowrap'
              }
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <InformationContent 
              scooter={scooter}
              isSuperAdmin={isSuperAdmin}
              onSignal={handleSignal}
              onLockToggle={handleLockToggle}
              onRestart={handleRestart}
              onDelete={handleDelete}
              onBatteryCover={handleBatteryCover}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <SettingsContent 
              scooter={scooter}
              open={open && activeTab === 1}
              onSave={handleSave}
              onBrokenStatusChange={handleBrokenStatusChange}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <HistoryContent scooterId={scooter?._id} open={open && activeTab === 2} />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <NotesContent scooterId={scooter?._id} open={open && activeTab === 3} />
          </TabPanel>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ScooterSettingsModal;
